import type { AnalyticsEventPayload, UIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { AnnotationUpdateEmitter } from '@atlaskit/editor-common/annotation';
import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types';
import { AnnotationMarkStates, AnnotationTypes } from '@atlaskit/adf-schema';

import { getApolloCache } from '@confluence/graphql';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { ExperienceStopStates } from '@confluence/experience-tracker';
import type { ExperienceTrackerAPI } from '@confluence/experience-tracker';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import { END } from '@confluence/navdex';
import {
	type AnalyticsSource,
	CommentType,
} from '@confluence/comments-util/entry-points/analytics';
import { fg } from '@confluence/feature-gating';
import { AnnotationStateQuery } from '@confluence/inline-comments-queries';
import type {
	AnnotationStateNode,
	AnnotationStateQueryType,
	AnnotationStateQueryVariables,
	InlineCommentLocation,
} from '@confluence/inline-comments-queries';

import { getTranslatedError, parseError, isUnexpectedError } from './inlineCommentsErrorUtils';
import { InlineCommentFramework } from './enum/InlineCommentFramework';
import { getTargetNodeType } from './analyticsUtils';

export const handleResolveSuccess = ({
	commentId,
	parentCommentMarkerRef,
	pageId,
	pageMode,
	eventEmitter,
	source,
	commentType,
	removeUnresolvedInlineComment,
	createAnalyticsEvent,
	getInlineNodeTypes,
	setEditorViewProps,
}: {
	commentId?: string;
	parentCommentMarkerRef: string;
	pageId: string;
	pageMode: PageMode;
	commentType: CommentType;
	eventEmitter?: AnnotationUpdateEmitter;
	source: AnalyticsSource;
	removeUnresolvedInlineComment?: (unresolvedAnnotationId: string | null) => void;
	createAnalyticsEvent: (payload: AnalyticsEventPayload) => UIAnalyticsEvent;
	getInlineNodeTypes?: (annotationId: string) => string[] | undefined;
	setEditorViewProps?: (viewProps: null) => void;
}) => {
	// Update the unresolved inline comments count
	removeUnresolvedInlineComment && removeUnresolvedInlineComment(parentCommentMarkerRef);

	createAnalyticsEvent({
		type: 'sendTrackEvent',
		data: {
			action: 'resolved',
			actionSubject: 'comment',
			actionSubjectId: commentId,
			objectType: 'page',
			objectId: pageId,
			source,
			attributes: {
				source,
				commentType,
				pageMode,
				framework: InlineCommentFramework.ANNOTATION_PROVIDER,
				navdexPointType: END,
				inlineNodeTypes:
					parentCommentMarkerRef && getInlineNodeTypes
						? getInlineNodeTypes(parentCommentMarkerRef)
						: undefined,
				targetNodeType: getTargetNodeType(parentCommentMarkerRef, pageMode !== PageMode.VIEW),
			},
		},
	}).fire();

	// Emit the event to mark the annotation as resolved
	if (pageMode === PageMode.VIEW) {
		eventEmitter?.emit(AnnotationUpdateEvent.SET_ANNOTATION_STATE, {
			[parentCommentMarkerRef]: {
				id: parentCommentMarkerRef,
				annotationType: AnnotationTypes.INLINE_COMMENT,
				state: AnnotationMarkStates.RESOLVED,
			},
		});
	} else {
		eventEmitter?.emit('resolve', parentCommentMarkerRef);
		setEditorViewProps && setEditorViewProps(null);
	}
};

export const handleDeleteSuccess = ({
	commentId,
	annotationId,
	pageId,
	pageMode,
	isReply,
	source,
	inlineNodeTypes,
	removeUnresolvedInlineComment,
	createAnalyticsEvent,
	onSuccess,
}: {
	commentId?: string;
	annotationId?: string;
	pageId: string;
	pageMode: PageMode;
	isReply: boolean;
	source: AnalyticsSource;
	inlineNodeTypes?: string[];
	createAnalyticsEvent: (payload: AnalyticsEventPayload) => UIAnalyticsEvent;
	removeUnresolvedInlineComment: (unresolvedAnnotationId: string | null) => void;
	onSuccess: (annotationId: string) => void;
}) => {
	if (!annotationId) {
		throw new Error('No annotationId for delete');
	}

	// Update the unresolved inline comments count
	removeUnresolvedInlineComment(annotationId);

	createAnalyticsEvent({
		type: 'sendTrackEvent',
		data: {
			action: 'deleted',
			actionSubject: 'comment',
			actionSubjectId: commentId,
			objectType: 'page',
			objectId: pageId,
			source,
			attributes: {
				commentType: CommentType.INLINE,
				source,
				pageMode,
				framework: InlineCommentFramework.ANNOTATION_PROVIDER,
				inlineNodeTypes,
				isReply,
				targetNodeType: getTargetNodeType(annotationId, pageMode !== PageMode.VIEW),
			},
		},
	}).fire();

	// Call the function passed in to do success specific things for each iteration
	onSuccess(annotationId);
};

// TODO: Move me and the translation logic to a shared component now that both
// general and inline comments use this logic to handle mutation failures
export const handleMutationFailure = ({
	experienceTracker,
	experienceName,
	error,
	commentId,
	shouldReturnError = false,
	shouldAbortForExpectedError = false,
	compoundExperienceName,
}: {
	experienceTracker: ExperienceTrackerAPI;
	experienceName: string;
	error: Error;
	commentId?: string;
	shouldReturnError?: boolean;
	shouldAbortForExpectedError?: boolean;
	compoundExperienceName?: string;
}) => {
	// get a truncated graphql error message
	const { message } = parseError(error);
	const translatedError = getTranslatedError(message, commentId);

	if (isUnexpectedError(translatedError)) {
		experienceTracker.stopOnError({
			name: experienceName,
			error,
		});

		/*
		 * stopOnError will fail the experience unless it's a session or network error, then it will be aborted.
		 * If the experience is failed and is a sub-experience, the compound experience will be failed automatically.
		 * If the experience is aborted and is a sub-experience, we need to abort the compound experience.
		 */
		if (compoundExperienceName && fg('confluence_frontend_improve_handle_mutation_fail')) {
			const experienceState = experienceTracker.getExperienceState(experienceName);

			if (experienceState?.stopState === ExperienceStopStates.TASK_ABORT) {
				experienceTracker.stopOnError({
					name: compoundExperienceName,
					error,
				});
			}
		}
	} else {
		// we currently display a generic message to the user whenever an error occurs
		// These errors will not be reported to splunk/signalfx through experience tracking as they are expected
		markErrorAsHandled(error);

		if (shouldAbortForExpectedError && fg('confluence_frontend_improve_handle_mutation_fail')) {
			// Ensure reason is always a string
			const reason =
				typeof translatedError.defaultMessage === 'string'
					? translatedError.defaultMessage
					: message;

			experienceTracker.abort({
				name: experienceName,
				reason,
			});

			// If a compound experience is also running, we need to abort it
			if (compoundExperienceName) {
				experienceTracker.abort({
					name: compoundExperienceName,
					reason,
				});
			}
		}
	}

	if (shouldReturnError) {
		// CommentEditor expects a translated error message otherwise it fails
		// TODO: CommentEditor should likely just receive the translated message
		// rather than expect the object needed for FormattedMessage
		return Promise.reject({
			error: translatedError,
		});
	}
};

export const updateAnnotationStateCache = ({
	pageId,
	commentId,
	markerRef,
	action,
}: {
	pageId: string;
	commentId?: string;
	markerRef?: string;
	action: 'create' | 'delete' | 'resolve' | 'reopen';
}) => {
	const cache = getApolloCache();

	const annotationStateData = cache.readQuery<
		AnnotationStateQueryType,
		AnnotationStateQueryVariables
	>({ query: AnnotationStateQuery, variables: { pageId } });

	const annotationStateNodes = (annotationStateData?.comments?.nodes || []).filter(
		(node) => node !== null,
	) as AnnotationStateNode[];
	const updatedAnnotationStateNodes = [...annotationStateNodes];

	if (action === 'create') {
		// Add a new element to the list using the markerRef with `{ resolved: false }`
		const newComment = {
			id: commentId!,
			location: {
				type: 'INLINE',
				inlineMarkerRef: markerRef,
				inlineResolveProperties: {
					resolved: false,
					__typename: 'InlineCommentResolveProperties',
				},
				__typename: 'InlineComment',
			},
			__typename: 'Comment',
		};

		updatedAnnotationStateNodes.push(newComment);
	} else if (action === 'delete') {
		// Remove the element from the list using the commentId to select
		const commentToRemoveIdx = updatedAnnotationStateNodes.findIndex(
			(node) => node.id === commentId,
		);

		if (commentToRemoveIdx !== -1) {
			updatedAnnotationStateNodes.splice(commentToRemoveIdx, 1);
		}
	} else if (action === 'resolve' || action === 'reopen') {
		// Update the resolved status from the list using the commentId to select based on the action
		const commentToUpdate = updatedAnnotationStateNodes.find((node) => node.id === commentId);
		if (
			commentToUpdate &&
			(commentToUpdate.location as InlineCommentLocation).inlineResolveProperties
		) {
			(commentToUpdate.location as InlineCommentLocation).inlineResolveProperties!.resolved =
				action === 'resolve';
		}
	}

	cache.writeQuery({
		query: AnnotationStateQuery,
		variables: { pageId },
		data: {
			...annotationStateData,
			comments: {
				...annotationStateData?.comments,
				nodes: updatedAnnotationStateNodes,
			},
		},
	});
};
